import React, { useCallback, useRef, useState, useEffect } from "react";
import { graphql } from "gatsby";

import useScreenSize, {
  SCREEN_SIZE_TYPES,
  isScreenSizeGreaterThan,
} from "hooks/useScreenSize";
import getBackground from "components/Backgrounds";

import ButtonsContainer from "../ButtonsContainer";

import * as styles from "./SectionHero.module.less";

const LG_GRADIENT = `linear-gradient(rgb(255, 255, 255) 10.82%, rgba(255, 255, 255, 0) 55%)`;
// These presets are only used for initial render when the "gradientEndPosition" might be undefined
const BG_GRADIENT_MAP = {
  [SCREEN_SIZE_TYPES.XXS]: `linear-gradient(rgb(255, 255, 255) 73.82%, rgba(255, 255, 255, 0) 88.27%)`,
  [SCREEN_SIZE_TYPES.XS]: `linear-gradient(rgb(255, 255, 255) 78.82%, rgba(255, 255, 255, 0) 84.27%)`,
  [SCREEN_SIZE_TYPES.SM]: `linear-gradient(rgb(255, 255, 255) 53.82%, rgba(255, 255, 255, 0) 84%)`,
  [SCREEN_SIZE_TYPES.MD]: LG_GRADIENT,
  [SCREEN_SIZE_TYPES.LG]: LG_GRADIENT,
  [SCREEN_SIZE_TYPES.XL]: LG_GRADIENT,
  [SCREEN_SIZE_TYPES.XXL]: LG_GRADIENT,
};

const XL_BG_IMAGE_PROPERTY = `right 0 bottom/57rem no-repeat`;
const BG_IMAGE_PROPERTY_MAP = {
  [SCREEN_SIZE_TYPES.XXS]: `center bottom -30px/34rem no-repeat`,
  [SCREEN_SIZE_TYPES.XS]: `right bottom/33rem no-repeat`,
  [SCREEN_SIZE_TYPES.SM]: `left 35vw bottom/36rem no-repeat`,
  [SCREEN_SIZE_TYPES.MD]: `left 20vw bottom/65rem no-repeat`,
  [SCREEN_SIZE_TYPES.LG]: XL_BG_IMAGE_PROPERTY,
  [SCREEN_SIZE_TYPES.XL]: XL_BG_IMAGE_PROPERTY,
  [SCREEN_SIZE_TYPES.XXL]: XL_BG_IMAGE_PROPERTY,
};

/**
 * @param {SCREEN_SIZE_TYPES} screenSize
 * @param {number} [gradientEndPosition]
 */
function getBackgroundGradient(screenSize, gradientEndPosition) {
  if (!gradientEndPosition) return BG_GRADIENT_MAP[screenSize];

  if (isScreenSizeGreaterThan(screenSize, SCREEN_SIZE_TYPES.SM)) {
    return LG_GRADIENT;
  }

  return `linear-gradient(rgb(255, 255, 255) ${gradientEndPosition}px, rgba(255, 255, 255, 0) ${
    gradientEndPosition + 49
  }px)`;
}

/**
 * We use this to define what will be queried for this component
 * This also serves as typings (See `Gatsby.SectionHeroDataFragment` below)
 */
export const SectionHeroDataFragment = graphql`
  fragment SectionHeroData on MarkdownRemarkFrontmatterSections {
    background
    title
    preamble
    ...SectionContentButtonsData
    section_id
  }
`;

/**
 * @param {GatsbyTypes.SectionHeroDataFragment} props
 */
export default function SectionHero({
  background,
  title,
  preamble,
  buttons,
  section_id,
}) {
  const [gradientEndPosition, setGradientEndPosition] = useState(undefined);
  const screenSize = useScreenSize();
  const contentRef = useRef(null);

  useEffect(() => {
    const windowResizeEventHandler = () => {
      const newGradientEndPosition =
        contentRef.current.offsetTop + contentRef.current.offsetHeight;

      return setGradientEndPosition(newGradientEndPosition);
    };

    window.addEventListener("resize", windowResizeEventHandler);

    return () => window.removeEventListener("resize", windowResizeEventHandler);
  }, []);

  const onButtonsContainerWrapperRefSet = useCallback((node) => {
    if (node) {
      if (contentRef.current) return;
      contentRef.current = node;

      const newGradientEndPosition = node.offsetTop + node.offsetHeight;
      return setGradientEndPosition(newGradientEndPosition);
    }
  }, []);

  const backgroundStyle = {
    background:
      getBackgroundGradient(screenSize, gradientEndPosition) +
      `, ` +
      `url("${getBackground(
        // @ts-expect-error
        background
      )}") ${BG_IMAGE_PROPERTY_MAP[screenSize]}`,
  };

  return (
    <section
      id={section_id}
      className={styles.sectionHero}
      style={gradientEndPosition ? backgroundStyle : null}
    >
      <div className={styles.content}>
        <h1>{title}</h1>
        <p>{preamble}</p>
        <div ref={onButtonsContainerWrapperRefSet}>
          <ButtonsContainer buttons={buttons} />
        </div>
      </div>
    </section>
  );
}
